import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search } from 'lucide-react';
import { showGlobalNotification } from '../hooks/useNotifications';

const API_BASE_URL = import.meta.env.VITE_API_URL;

interface Brand {
    id: number;
    name: string;
    is_active: boolean;
}

interface Model {
    id: number;
    brand_id: number;
    name: string;
    image_url?: string;
    processor?: string;
    graphics_card?: string;
    wifi_support?: string;
    battery_info?: string;
    screen_resolution?: string;
    storage_capacity?: string;
    storage_type?: string;
    ram?: string;
    description?: string;
    is_active: boolean;
    brand_name?: string;
}

const ComputerModelsPage: React.FC = () => {
    const [models, setModels] = useState<Model[]>([]);
    const [brands, setBrands] = useState<Brand[]>([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingModel, setEditingModel] = useState<Model | null>(null);
    const [searchTerm, setSearchTerm] = useState('');

    const [modelData, setModelData] = useState({
        brand_id: null as number | null,
        name: '',
        processor: '',
        graphics_card: '',
        wifi_support: '',
        battery_info: '',
        screen_resolution: '',
        storage_capacity: '',
        storage_type: '',
        ram: '',
        description: '',
        image: null as File | null,
        is_active: true
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('auth_token');

            const [modelsRes, brandsRes] = await Promise.all([
                fetch(`${API_BASE_URL}/computers/computer-models`, { headers: { 'Authorization': `Bearer ${token}` } }),
                fetch(`${API_BASE_URL}/computers/computer-brands`, { headers: { 'Authorization': `Bearer ${token}` } })
            ]);

            const [modelsData, brandsData] = await Promise.all([
                modelsRes.json(),
                brandsRes.json()
            ]);

            setModels(Array.isArray(modelsData) ? modelsData : []);
            setBrands(Array.isArray(brandsData) ? brandsData : []);
        } catch (error) {
            console.error('Error loading data:', error);
            showGlobalNotification.error('Hata', 'Veriler yüklenirken hata oluştu');
            setModels([]);
            setBrands([]);
        } finally {
            setLoading(false);
        }
    };

    const resetModelForm = () => {
        setModelData({
            brand_id: null,
            name: '',
            processor: '',
            graphics_card: '',
            wifi_support: '',
            battery_info: '',
            screen_resolution: '',
            storage_capacity: '',
            storage_type: '',
ram: '',
            description: '',
            image: null,
            is_active: true
        });
        setEditingModel(null);
    };

    const handleSaveModel = async () => {
        if (!modelData.brand_id || !modelData.name.trim()) {
            showGlobalNotification.error('Hata', 'Marka ve Model adı zorunludur');
            return;
        }

        try {
            const token = localStorage.getItem('auth_token');
            const formData = new FormData();
            formData.append('brand_id', modelData.brand_id.toString());
            formData.append('name', modelData.name);
            formData.append('processor', modelData.processor);
            formData.append('graphics_card', modelData.graphics_card);
            formData.append('wifi_support', modelData.wifi_support);
            formData.append('battery_info', modelData.battery_info);
            formData.append('screen_resolution', modelData.screen_resolution);
            formData.append('storage_capacity', modelData.storage_capacity);
            formData.append('storage_type', modelData.storage_type);
            formData.append('ram', modelData.ram);
            formData.append('description', modelData.description);
            formData.append('is_active', modelData.is_active.toString());
            if (modelData.image) {
                formData.append('image', modelData.image);
            }

            const url = editingModel
                ? `${API_BASE_URL}/computers/computer-models/${editingModel.id}`
                : `${API_BASE_URL}/computers/computer-models`;

            const response = await fetch(url, {
                method: editingModel ? 'PUT' : 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Kayıt başarısız');
            }

            showGlobalNotification.success('Başarılı', editingModel ? 'Model güncellendi' : 'Model eklendi');
            setShowModal(false);
            resetModelForm();
            loadData();
        } catch (error: any) {
            showGlobalNotification.error('Hata', error.message);
        }
    };

    const handleEditModel = (model: Model) => {
        setEditingModel(model);
        setModelData({
            brand_id: model.brand_id,
            name: model.name,
            processor: model.processor || '',
            graphics_card: model.graphics_card || '',
            wifi_support: model.wifi_support || '',
            battery_info: model.battery_info || '',
            screen_resolution: model.screen_resolution || '',
            storage_capacity: model.storage_capacity || '',
            storage_type: model.storage_type || '',
            ram: model.ram || '',
            description: model.description || '',
            image: null,
            is_active: model.is_active
        });
        setShowModal(true);
    };

    const handleDeleteModel = async (id: number) => {
        if (!confirm('Bu modeli silmek istediğinizden emin misiniz?')) return;

        try {
            const token = localStorage.getItem('auth_token');
            const response = await fetch(`${API_BASE_URL}/computers/computer-models/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Silme başarısız');
            }

            showGlobalNotification.success('Başarılı', 'Model silindi');
            loadData();
        } catch (error: any) {
            showGlobalNotification.error('Hata', error.message);
        }
    };
    
    const filteredModels = models.filter(model =>
        (model.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (model.brand_name && model.brand_name.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    if (loading) {
        return <div className="flex justify-center items-center h-screen"><div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-primary"></div></div>;
    }

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold">Bilgisayar Modelleri</h1>
                <button
                    onClick={() => { resetModelForm(); setShowModal(true); }}
                    className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark flex items-center"
                >
                    <Plus className="w-5 h-5 mr-2" />
                    Yeni Model Ekle
                </button>
            </div>

            <div className="mb-4">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                        type="text"
                        placeholder="Model veya marka ara..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border rounded-lg"
                    />
                </div>
            </div>

            <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Görsel</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Marka</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Model</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">İşlemci</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">RAM</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Depolama</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Durum</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">İşlemler</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {filteredModels.map(model => (
                            <tr key={model.id}>
                                <td className="px-6 py-4">
                                    {model.image_url && (
                                        <img src={`${API_BASE_URL.replace('/api', '')}${model.image_url}`} alt={model.name} className="w-16 h-16 object-contain" />
                                    )}
                                </td>
                                <td className="px-6 py-4">{model.brand_name}</td>
                                <td className="px-6 py-4">{model.name}</td>
                                <td className="px-6 py-4">{model.processor}</td>
                                <td className="px-6 py-4">{model.ram}</td>
                                <td className="px-6 py-4">{model.storage_capacity} {model.storage_type}</td>
                                <td className="px-6 py-4">
                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${model.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                        {model.is_active ? 'Aktif' : 'Pasif'}
                                    </span>
                                </td>
                                <td className="px-6 py-4">
                                    <button onClick={() => handleEditModel(model)} className="text-primary hover:text-primary-dark mr-4"><Edit className="w-5 h-5" /></button>
                                    <button onClick={() => handleDeleteModel(model.id)} className="text-red-600 hover:text-red-900"><Trash2 className="w-5 h-5" /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {showModal && (
                 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                 <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                     <div className="p-6">
                         <div className="flex justify-between items-center mb-4">
                             <h2 className="text-xl font-bold">{editingModel ? 'Modeli Düzenle' : 'Yeni Model Ekle'}</h2>
                             <button onClick={() => { setShowModal(false); resetModelForm(); }} className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300">&times;</button>
                         </div>
                         <div className="space-y-4">
                             <div>
                                 <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Marka *</label>
                                 <select
                                     value={modelData.brand_id || ''}
                                     onChange={(e) => setModelData({ ...modelData, brand_id: e.target.value ? parseInt(e.target.value) : null })}
                                     className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                                 >
                                     <option value="">Marka seçiniz...</option>
                                     {brands.filter(b => b.is_active).map(brand => (
                                         <option key={brand.id} value={brand.id}>{brand.name}</option>
                                     ))}
                                 </select>
                             </div>
                             <div>
                                 <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Model Adı *</label>
                                 <input
                                     type="text"
                                     value={modelData.name}
                                     onChange={(e) => setModelData({ ...modelData, name: e.target.value })}
                                     className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                                 />
                             </div>
                             <div>
                                 <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">İşlemci</label>
                                 <input
                                     type="text"
                                     value={modelData.processor}
                                     onChange={(e) => setModelData({ ...modelData, processor: e.target.value })}
                                     className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                                 />
                             </div>
                             <div>
                                 <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Ekran Kartı</label>
                                 <input
                                     type="text"
                                     value={modelData.graphics_card}
                                     onChange={(e) => setModelData({ ...modelData, graphics_card: e.target.value })}
                                     className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                                 />
                             </div>
                             <div>
                                 <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">RAM</label>
                                 <input
                                     type="text"
                                     value={modelData.ram}
                                     onChange={(e) => setModelData({ ...modelData, ram: e.target.value })}
                                     className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                                 />
                             </div>
                             <div>
                                 <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Depolama Alanı</label>
                                 <input
                                     type="text"
                                     value={modelData.storage_capacity}
                                     onChange={(e) => setModelData({ ...modelData, storage_capacity: e.target.value })}
                                     className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                                 />
                             </div>
                             <div>
                                 <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Depolama Tipi</label>
                                 <input
                                     type="text"
                                     value={modelData.storage_type}
                                     onChange={(e) => setModelData({ ...modelData, storage_type: e.target.value })}
                                     className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                                 />
                             </div>
                             <div>
                                 <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Görsel</label>
                                 <input
                                     type="file"
                                     accept="image/*"
                                     onChange={(e) => setModelData({ ...modelData, image: e.target.files?.[0] || null })}
                                     className="mt-1 block w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary/10 file:text-primary-dark hover:file:bg-primary/20"
                                 />
                             </div>
                             <div className="flex items-center">
                                 <input
                                     type="checkbox"
                                     id="model_is_active"
                                     checked={modelData.is_active}
                                     onChange={(e) => setModelData({ ...modelData, is_active: e.target.checked })}
                                     className="h-4 w-4 text-primary focus:ring-primary border-gray-300 dark:border-gray-600 rounded"
                                 />
                                 <label htmlFor="model_is_active" className="ml-2 block text-sm text-gray-900 dark:text-gray-100">Aktif</label>
                             </div>
                         </div>
                         <div className="mt-6 flex justify-end space-x-3">
                             <button
                                 onClick={() => { setShowModal(false); resetModelForm(); }}
                                 className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-300"
                             >
                                 İptal
                             </button>
                             <button
                                 onClick={handleSaveModel}
                                 className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
                             >
                                 {editingModel ? 'Güncelle' : 'Kaydet'}
                             </button>
                         </div>
                     </div>
                 </div>
             </div>
            )}
        </div>
    );
};

export default ComputerModelsPage;
